import { z } from 'zod'

/** CRM shared types: workstream G (export, erasure, retention, organisation consent, summaries). */

// ---------------------------------------------------------------------------
// Consent per purpose and channel (design 7.2): append-only, the latest row wins.
// ---------------------------------------------------------------------------
/** An organisation's consent across its people (screen 5 mockup "Consent: Service, marketing"). */
export interface OrgConsentView {
  people: number
  purposes: { purpose: string; channel: string; granted: number; revoked: number }[]
}

// ---------------------------------------------------------------------------
// AI summaries (design 7.4)
// ---------------------------------------------------------------------------
export interface SummaryStatusView {
  summary: string | null
  /** When the summary was written; null when there is none yet. */
  summary_at: string | null
  /** A refresh is waiting for new activity to settle. */
  due_at: string | null
  /** The last refresh failed: the summary shown is from summary_at. */
  error: string | null
}

// ---------------------------------------------------------------------------
// Export and erasure (design 17.4)
// ---------------------------------------------------------------------------
/** One person's data, everything held, as one JSON document (GET /crm/people/:id/export). */
export interface PersonExport {
  format: 'takeit4me.person-export.v1'
  exported_at: string
  exported_by: string
  company: { id: string; name: string }
  person: Record<string, unknown>
  [section: string]: unknown
}

/** What erasing a person removes or keeps, by table (the dry run and the confirmation use the same shape). */
export interface ErasureSummary {
  person_id: string
  /** Rows deleted or emptied, by what they are. */
  removed: Record<string, number>
  /** Rows kept with the person anonymised, and why. */
  kept: { what: string; count: number; why: string }[]
  /** Addresses that stay blocked so they are never contacted again (kept as suppressions). */
  suppressed: number
  /** Stored files (recordings, attachments) that operators must delete from storage by hand. */
  storage_left: number
  /** Reasons the erasure cannot go ahead now (a payment in flight, a live call). Empty when it can. */
  blocked: string[]
}
export interface ErasureResult extends ErasureSummary { erased_at: string; request_id: string }
export const ErasureConfirm = z.object({
  /** The person's current name, typed by the admin to confirm. */
  confirm_name: z.string().min(1),
  reason: z.string().trim().min(3).max(500),
})

// ---------------------------------------------------------------------------
// Retention (design 17.4)
// ---------------------------------------------------------------------------
export interface RetentionStatusView {
  retention_days: number
  cutoff: string
  last_run: { started_at: string; finished_at: string | null; complete: boolean; counts: Record<string, number>; error: string | null } | null
  /** Content older than the cutoff that is still held (the purge works through it in batches). */
  backlog: Record<string, number>
  /** What retention removes and what it keeps, in plain words. */
  policy: { removes: string[]; keeps: string[] }
}
export interface PrivacyRequestView { id: string; kind: 'export' | 'erasure'; person_id: string; staff: string | null; counts: Record<string, number>; bytes: number | null; at: string }
